// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useState, useCallback, useEffect, useRef, type UIEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import ContinueButton from '@components/continueButton/ContinueButton';
import { getConsentLegalDocumentContent } from '@api/selfServiceServices';
import { JsonToHtml } from '@imprivata-cloud/components';

export interface ConsentDocumentScreenProps {
  title?: string;
  description?: string;
  documentUrl?: string;
  onContinue: () => void;
  onCancel: () => void;
  continueLabel?: string;
  cancelLabel?: string;
  showConfirmationModal?: boolean;
}

const ConsentDocumentScreen = ({
  title,
  description,
  documentUrl,
  onContinue,
  onCancel,
  continueLabel,
  cancelLabel,
}: ConsentDocumentScreenProps) => {
  const { t } = useTranslation();
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const jsonUrl = documentUrl?.replace(/\.html$/, '.json');

  const { data, isLoading } = useQuery({
    queryKey: ['consent-document', jsonUrl],
    queryFn: () => getConsentLegalDocumentContent(jsonUrl || ''),
    enabled: Boolean(documentUrl),
    retry: 3,
    refetchOnWindowFocus: false
  });

  const [canContinue, setCanContinue] = useState(!documentUrl);

  useEffect(() => {
    if (!documentUrl) {
      return;
    }

    if (!data || isLoading) {
      setCanContinue(false);
      return;
    }

    const element = scrollElementRef.current;
    if (!element) {
      return;
    }

    const checkScrollability = () => {
      const needsScrolling = element.scrollHeight > element.clientHeight;
      if (!needsScrolling) {
        setCanContinue(true);
      }
    };

    checkScrollability();

    const resizeObserver = new ResizeObserver(() => {
      checkScrollability();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [data, isLoading, documentUrl]);

  const handleScroll: UIEventHandler<HTMLDivElement> = useCallback((e) => {
    const element = e.target as HTMLDivElement;
    const isAtBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 1;
    if (isAtBottom) {
      setCanContinue(true);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="consent-document-screen__loading">
        <Spin indicator={<LoadingOutlined spin />} />
        <span className="consent-document-screen__loading-text">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="consent-screens">
      <div className="consent-screens__content">
        <h2 className="consent-screens__title">{title ?? t('self-service.consent-screens.title')}</h2>
        {!!description && <p className="consent-screens__description">{description}</p>}
        {!!documentUrl && data && (
          <div ref={scrollElementRef} onScroll={handleScroll} className="consent-text">
            <JsonToHtml json={data} />
          </div>
        )}
      </div>

      <div className="consent-screens__actions">
        <ContinueButton
          className="consent-screens__continue-button"
          aria-label="continue-button"
          onClick={onContinue}
          disabled={isLoading || !canContinue}
          secondaryButton={{
            'aria-label': 'cancel-button',
            label: cancelLabel ?? t('self-service.consent-screens.decline-consent'),
            onClick: onCancel,
          }}
        >
          {continueLabel ?? t('actions.agree')}
        </ContinueButton>
      </div>
    </div>
  );
};

export default ConsentDocumentScreen;
